import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { SponsoringFilter } from "./dashboardFilters";
import { useDashboardFilters } from "./useDashboardFilters";
import { useSponsoringCount } from "./useSponsoringCount";

type Tone = "neutral" | "destructive" | "warning";

const toneClasses: Record<Tone, string> = {
  neutral: "text-brand-blue",
  destructive: "text-destructive",
  warning: "text-orange-500",
};

type Tile = {
  /** Suffix of the `crm.dashboard.stats.*` translation key */
  name: string;
  filter: SponsoringFilter;
  tone: Tone;
  /** Where the tile links to; `?filter=` pre-selects a quick filter of the list */
  to: string;
  /** The highlighted (filled) tile */
  primary?: boolean;
};

/**
 * Six counters summarising the sponsoring portfolio. Every tile asks the
 * backend for the total of one filtered query; nothing is computed in the
 * browser.
 */
export const SponsoringStats = ({
  className,
}: {
  /** Extra classes for the grid wrapper, e.g. to change the column count. */
  className?: string;
}) => {
  const { filters } = useDashboardFilters();

  const tiles: Tile[] = [
    {
      name: "active",
      filter: filters.active,
      tone: "neutral",
      to: "/sponsorings?filter=active",
      primary: true,
    },
    {
      name: "expired",
      filter: filters.expired,
      tone: "destructive",
      to: "/sponsorings?filter=expired",
    },
    {
      name: "within_30",
      filter: filters.within30,
      tone: "warning",
      to: "/sponsorings?filter=ending_soon",
    },
    {
      name: "within_90",
      filter: filters.within90,
      tone: "neutral",
      to: "/sponsorings?filter=ending_soon",
    },
    {
      name: "without_end_date",
      filter: filters.withoutEndDate,
      tone: "neutral",
      to: "/sponsorings",
    },
    {
      name: "open_leads",
      filter: filters.openLeads,
      tone: "neutral",
      to: "/sponsorings",
    },
  ];

  return (
    <div
      className={cn("grid grid-cols-2 gap-4 md:grid-cols-3", className)}
      data-testid="sponsoring-stats"
    >
      {tiles.map((tile) => (
        <StatTile key={tile.name} {...tile} />
      ))}
    </div>
  );
};

const StatTile = ({ name, filter, tone, to, primary = false }: Tile) => {
  const translate = useTranslate();
  const { count, isPending } = useSponsoringCount(filter);
  const label = translate(`crm.dashboard.stats.${name}`);

  return (
    <Link
      to={to}
      className={cn(
        "flex h-full flex-col gap-3 rounded-xl border p-4 no-underline transition-shadow hover:shadow-md",
        primary
          ? "border-brand-navy bg-brand-navy text-white"
          : "border-border bg-white text-foreground",
      )}
      data-testid={`sponsoring-stat-${name}`}
      aria-label={`${label}: ${count}`}
    >
      <span
        className={cn(
          "text-xs leading-tight",
          primary ? "text-white/80" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
      {isPending ? (
        <Skeleton className="h-8 w-12" />
      ) : (
        <span
          className={cn(
            "text-3xl font-bold leading-none tabular-nums",
            primary
              ? "text-white"
              : count > 0
                ? toneClasses[tone]
                : toneClasses.neutral,
          )}
          data-testid={`sponsoring-stat-${name}-value`}
        >
          {count}
        </span>
      )}
    </Link>
  );
};
